import { TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { Box, Button, Icon } from "../../common/components";
import DialogTitleWithCloseButton from "../../common/components/Dialog/DialogTitleWithCloseButton/DialogTitleWithCloseButton";
import { palette, spacing } from "../../styles/theme";
import { ROUTES } from "../../constant/common.constant";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { Team } from "../../types";
import ProjectService from "../../service/ProjectService";
import { useProject } from "../../context/Project/useProject";
import { useTeam } from "../../context/Team/useTeam";

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

const MoveToTeamModal = ({ open, onClose, projectId }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const { refetchProject } = useProject();
  const { refetchTeams } = useTeam();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const handleClose = () => {
    setSelectedTeam(null);
    onClose();
  };

  const { teams } = useTeam();

  const navigate = useNavigate();
  const onAddToTeam = useCallback(
    async (newTeamId?: string) => {
      await ProjectService.moveProject(projectId, newTeamId ?? "");
      await refetchProject({ skipLoading: true });
      await refetchTeams({ skipLoading: true });
      navigate(generatePath(ROUTES.TEAM, { teamId: newTeamId }));
    },
    [navigate, projectId, refetchProject, refetchTeams]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"md"}
      fullScreen={isMobile}
    >
      <DialogTitleWithCloseButton
        id="customized-dialog-title"
        onClose={handleClose}
      >
        {t("moveToTeamModal.title")}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
        >
          <Box>
            <Autocomplete
              id="moveProjects"
              options={teams}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} />}
              clearIcon={false}
              popupIcon={
                <Icon size={24} color={palette.grey.primary} icon="search" />
              }
              filterOptions={(options, state) => {
                const value = state.inputValue.toUpperCase();
                return options.filter((o) =>
                  o.name.toUpperCase().includes(value)
                );
              }}
              value={selectedTeam}
              onChange={(event, newValue) => {
                setSelectedTeam(newValue);
              }}
              getOptionDisabled={(option) => {
                return option.id === projectId;
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.name + option.id}>
                    {option.name}
                  </li>
                );
              }}
              sx={{
                "& .MuiAutocomplete-popupIndicator": { transform: "none" },
              }}
            />
          </Box>
          {isMobile && (
            <Button
              onClick={() => onAddToTeam(selectedTeam?.id)}
              disabled={selectedTeam === null}
              fullWidth
            >
              <Typography variant="actionBoldBigIcon">
                {t("moveToTeamModal.action")}
              </Typography>
            </Button>
          )}
        </Box>
      </DialogContent>
      {!isMobile && (
        <DialogActions>
          <Button
            onClick={() => onAddToTeam(selectedTeam?.id)}
            disabled={selectedTeam === null}
          >
            <Typography variant="actionBoldBigIcon">
              {t("moveToTeamModal.action")}
            </Typography>
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(MoveToTeamModal);
